import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { getLabelData } from '../../api/AdminPage';
import {
  createEmaFormulation,
  createEmaRoute,
  getEmaFormulations,
  getEmaRoutes,
  markEmaProductsDone
} from '../../api/ema';
import GlobalStore from '../../store';
import GlobalActions from '../../store/actions';
import AuthContext from '../../store/Auth/AuthProvider';
import type { AsyncState, FormulationDto, RouteDto } from '../../types/dtos/ema';
import HttpStatus from '../../types/enum/http_status';
import { buildProductPayload, submitEmaProductForm } from './emaProductSubmit';
import { resolveEmaPutCurationId, toCurationId } from '../../utils/resolveEmaPutCurationId';
import { createInitialEmaProductsState, emaProductsReviewReducer } from './EMAProductsReview.state';
import type {
  EmaProductFormData,
  EmaProductsAuthContext,
  EmaProductsLoadResponse,
  EmaProductsLocationState,
  EmaProductsReviewModel,
  EmaProductsRouteParams,
  EmaProductsStoreContext
} from './EMAProductsReview.types';

const EMPTY_ASYNC_STATE = { data: [], loading: false, error: null };

/* eslint-disable no-unused-vars -- parameter names document the legacy request interface */
type GetEmaProductsLabelData = (
  applicationNumber: string,
  submission: null,
  submissionNumber: null,
  documentType: 'products'
) => Promise<EmaProductsLoadResponse>;
/* eslint-enable no-unused-vars */

const getEmaProductsLabelData = getLabelData as unknown as GetEmaProductsLabelData;

const useEMAProductsReview = (): EmaProductsReviewModel => {
  const history = useHistory();
  const location = useLocation<EmaProductsLocationState>();
  const { applNo: legacyApplNo, curationId } = useParams<EmaProductsRouteParams>();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const productNumber =
    location.state?.product_number ||
    searchParams.get('product_number') ||
    (legacyApplNo ? `EMEA-H-C-${legacyApplNo}` : '');
  const applicationNumber =
    location.state?.appl_num || legacyApplNo || productNumber.split('-').pop() || '';
  const hasCurationIdentity = Boolean(curationId && productNumber);

  const { currentUser } = useContext(AuthContext) as unknown as EmaProductsAuthContext;
  const { dispatch: globalDispatch } = useContext(
    GlobalStore
  ) as unknown as EmaProductsStoreContext;
  const [state, dispatch] = useReducer(
    emaProductsReviewReducer,
    undefined,
    createInitialEmaProductsState
  );
  const [formulations, setFormulations] = useState<AsyncState<FormulationDto>>(EMPTY_ASYNC_STATE);
  const [routes, setRoutes] = useState<AsyncState<RouteDto>>(EMPTY_ASYNC_STATE);
  const mountedRef = useRef(true);
  const requestVersionRef = useRef(0);

  const showAlert = useCallback(
    (message: string, color?: string) => {
      globalDispatch({
        type: GlobalActions.SET_ALERT,
        value: { status: true, message, color }
      });
    },
    [globalDispatch]
  );

  const fetchFormulations = useCallback(async () => {
    setFormulations(previous => ({ ...previous, loading: true, error: null }));
    try {
      const response = await getEmaFormulations();
      const data = response?.data?.body;
      if (!Array.isArray(data)) {
        throw new Error('Invalid response format');
      }
      if (mountedRef.current) {
        setFormulations({ data, loading: false, error: null });
      }
    } catch (error) {
      if (mountedRef.current) {
        const message = error instanceof Error ? error.message : 'Failed to fetch formulations';
        setFormulations(previous => ({ ...previous, loading: false, error: message }));
        showAlert('Failed to fetch formulations. Please retry.');
      }
    }
  }, [showAlert]);

  const fetchRoutes = useCallback(async () => {
    setRoutes(previous => ({ ...previous, loading: true, error: null }));
    try {
      const response = await getEmaRoutes();
      const data = response?.data?.body;
      if (!Array.isArray(data)) {
        throw new Error('Invalid response format');
      }
      if (mountedRef.current) {
        setRoutes({ data, loading: false, error: null });
      }
    } catch (error) {
      if (mountedRef.current) {
        const message = error instanceof Error ? error.message : 'Failed to fetch routes';
        setRoutes(previous => ({ ...previous, loading: false, error: message }));
        showAlert('Failed to fetch routes. Please retry.');
      }
    }
  }, [showAlert]);

  const loadProducts = useCallback(
    async (initializeCount: boolean) => {
      const requestVersion = requestVersionRef.current + 1;
      requestVersionRef.current = requestVersion;
      dispatch({ type: 'LOAD_STARTED' });

      if (!applicationNumber) {
        dispatch({ type: 'LOAD_FAILED' });
        showAlert('Product number is required to load EMA products.');
        return;
      }

      try {
        const response = await getEmaProductsLabelData(applicationNumber, null, null, 'products');
        if (!mountedRef.current || requestVersion !== requestVersionRef.current) {
          return;
        }

        const responseBody = response?.data?.Success;
        if (!responseBody?.pdf_s3_url) {
          dispatch({ type: 'LOAD_FAILED' });
          showAlert(`No data found for the application number EMEA-H-C-${applicationNumber}`);
          return;
        }

        dispatch({
          type: 'LOAD_SUCCEEDED',
          value: {
            products: (responseBody.products_qc ?? []).map(product => ({
              ...product,
              curation_id: toCurationId(product.curation_id)
            })),
            pdfUrl: responseBody.pdf_s3_url,
            productUrl: responseBody.pdf_s3_path ?? '',
            initializeCount
          }
        });
      } catch {
        if (mountedRef.current && requestVersion === requestVersionRef.current) {
          dispatch({ type: 'LOAD_FAILED' });
          showAlert('Unable to load EMA products.');
        }
      }
    },
    [applicationNumber, showAlert]
  );

  useEffect(() => {
    mountedRef.current = true;
    loadProducts(true);
    fetchFormulations();
    fetchRoutes();

    return () => {
      mountedRef.current = false;
      requestVersionRef.current += 1;
    };
  }, [fetchFormulations, fetchRoutes, loadProducts]);

  const addFormulation = useCallback(
    async (value: string) => {
      const result = await createEmaFormulation(
        value,
        currentUser?.email ?? '',
        currentUser?.['custom:user'] ?? ''
      );
      if ('error' in result) {
        throw new Error(result.error);
      }
      if (mountedRef.current) {
        setFormulations(previous => ({
          ...previous,
          data: [...previous.data, result].sort((a, b) => a.value.localeCompare(b.value))
        }));
      }
      return result;
    },
    [currentUser]
  );

  const addRoute = useCallback(
    async (value: string) => {
      const result = await createEmaRoute(
        value,
        currentUser?.email ?? '',
        currentUser?.['custom:user'] ?? ''
      );
      if ('error' in result) {
        throw new Error(result.error);
      }
      if (mountedRef.current) {
        setRoutes(previous => ({
          ...previous,
          data: [...previous.data, result].sort((a, b) => a.value.localeCompare(b.value))
        }));
      }
      return result;
    },
    [currentUser]
  );

  const selectedProduct = useMemo(
    () => state.products.find(product => product.id === state.selectedProductId) || null,
    [state.products, state.selectedProductId]
  );

  const selectProduct = useCallback((productId: string | null) => {
    dispatch({ type: 'SELECT_PRODUCT', value: productId });
  }, []);

  const startAdd = useCallback(() => {
    dispatch({ type: 'SET_MODE', value: 'add' });
  }, []);

  const startEdit = useCallback(() => {
    dispatch({ type: 'SET_MODE', value: 'edit' });
  }, []);

  const requestSubmit = useCallback(
    (formData: EmaProductFormData) => {
      if (!hasCurationIdentity) {
        showAlert('Curation ID and product number are required to save EMA products.');
        return;
      }
      if (!formData.indication_status) {
        showAlert('Please update Status before you save.');
        return;
      }
      dispatch({ type: 'OPEN_SUBMIT_DIALOG', value: formData });
    },
    [hasCurationIdentity, showAlert]
  );

  const requestCancel = useCallback(() => {
    dispatch({ type: 'OPEN_DIALOG', value: 'cancel' });
  }, []);

  const requestDone = useCallback(() => {
    dispatch({ type: 'OPEN_DIALOG', value: 'done' });
  }, []);

  const closeDialog = useCallback(() => {
    dispatch({ type: 'CLOSE_DIALOG' });
  }, []);

  const saveProduct = useCallback(async () => {
    const formData = state.pendingForm;
    if (!formData || !curationId || !hasCurationIdentity) {
      return;
    }

    const wasAdd = state.mode === 'add';
    const editingProduct = wasAdd
      ? null
      : state.products.find(product => product.id === state.selectedProductId) ?? null;
    const lookupKeyUnchanged = Boolean(
      editingProduct && editingProduct.pno.trim() === formData.pno.trim()
    );
    const putCurationId = resolveEmaPutCurationId(
      curationId,
      toCurationId(editingProduct?.curation_id),
      lookupKeyUnchanged
    );
    dispatch({ type: 'CLOSE_DIALOG' });
    dispatch({ type: 'SET_LOADING', value: true });
    const payload = buildProductPayload(
      lookupKeyUnchanged && editingProduct ? { ...formData, pno: editingProduct.pno } : formData,
      productNumber,
      putCurationId,
      state.productUrl,
      currentUser?.email ?? '',
      currentUser?.['custom:user'] ?? ''
    );
    const result = await submitEmaProductForm(productNumber, putCurationId, payload);
    if (!mountedRef.current) {
      return;
    }
    if (!result.ok) {
      dispatch({ type: 'SET_LOADING', value: false });
      showAlert(result.error);
      return;
    }

    dispatch({ type: 'SAVE_SUCCEEDED', productId: result.productId, wasAdd });
    showAlert('Saved Successfully!', 'success');
    await loadProducts(false);
  }, [
    curationId,
    currentUser,
    hasCurationIdentity,
    loadProducts,
    productNumber,
    showAlert,
    state.mode,
    state.pendingForm,
    state.productUrl,
    state.products,
    state.selectedProductId
  ]);

  const completeProducts = useCallback(async () => {
    if (!curationId || !hasCurationIdentity) {
      showAlert('Curation ID and product number are required to complete EMA products.');
      return;
    }

    dispatch({ type: 'CLOSE_DIALOG' });
    dispatch({ type: 'SET_LOADING', value: true });
    const response = await markEmaProductsDone(productNumber, curationId);
    if (!mountedRef.current) {
      return;
    }
    if (response?.status !== HttpStatus.OK) {
      const responseBody = response?.data?.body;
      dispatch({ type: 'SET_LOADING', value: false });
      showAlert(
        (typeof responseBody === 'string' && responseBody) ||
          response?.data?.message ||
          'Unable to complete EMA products.'
      );
      return;
    }

    dispatch({ type: 'SET_LOADING', value: false });
    showAlert('Products curation completed successfully!', 'success');
    history.push('/ema/curations');
  }, [curationId, hasCurationIdentity, history, productNumber, showAlert]);

  const confirmDialog = useCallback(async () => {
    if (state.dialog === 'submit') {
      await saveProduct();
      return;
    }
    if (state.dialog === 'done') {
      await completeProducts();
      return;
    }
    if (state.dialog === 'cancel') {
      dispatch({ type: 'SET_MODE', value: 'view' });
      dispatch({ type: 'CLOSE_DIALOG' });
    }
  }, [completeProducts, saveProduct, state.dialog]);

  const goBack = useCallback(() => {
    history.push('/ema/curations');
  }, [history]);

  const actions = useMemo(
    () => ({
      goBack,
      startAdd,
      startEdit,
      selectProduct,
      requestSubmit,
      requestCancel,
      requestDone,
      closeDialog,
      confirmDialog,
      fetchFormulations,
      fetchRoutes,
      addFormulation,
      addRoute
    }),
    [
      addFormulation,
      addRoute,
      closeDialog,
      confirmDialog,
      fetchFormulations,
      fetchRoutes,
      goBack,
      requestCancel,
      requestDone,
      requestSubmit,
      selectProduct,
      startAdd,
      startEdit
    ]
  );

  return useMemo(
    () => ({
      productNumber,
      applicationNumber,
      hasCurationIdentity,
      currentUser,
      state,
      selectedProduct,
      resources: { formulations, routes },
      actions
    }),
    [
      actions,
      applicationNumber,
      currentUser,
      formulations,
      hasCurationIdentity,
      productNumber,
      routes,
      selectedProduct,
      state
    ]
  );
};

export default useEMAProductsReview;
